import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';

interface ImageCategory {
  live: string[];
  honoured: { image: string; caption: string }[];
  celebrities: { image: string; caption: string }[];
}

const Moments: React.FC = () => {
  const [images, setImages] = useState<ImageCategory>({ 
    live: [], 
    honoured: [], 
    celebrities: [] 
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    // Organize images by category
    const imagesByCategory: ImageCategory = {
      live: [
        'Live BPL solo.png',
        'Live Mumbai CG.png',
        'Live Borivali.png',
        'Live Mumbai CG2.png',
        'Live KIng circle.png',
        'Live BPL2.png',
        'Live BPL.png',
        'Live KingCircle2.png',
        'Live Powai.png',
        'Live VIkroli.png',
      ],
      honoured: [
        { image: 'Vishwas Sarang.png', caption: 'With Hon. Minister Shri Vishwas Sarang' },
      ],
      celebrities: [
        { image: 'Dipti Naval.png', caption: 'With legendary actress Deepti Naval' },
        { image: 'Jolly Mukharjee.png', caption: 'With playback singer Jolly Mukherjee' },
      ],
    };
    
    setImages(imagesByCategory);
  }, []);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20 px-4">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-12 text-center">
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4 pb-2">
            ✨ Moments
          </h1>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            Music creates memories that time can never fade
          </p>
        </div>

        {/* Gallery by Category */}
        <div className="max-w-6xl mx-auto space-y-16">
          {/* Live Section */}
          {images.live.length > 0 && (
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
                🎤 Live
              </h2>
              <div className="space-y-8">
                {images.live.map((image, index) => (
                  <div
                    key={`live-${index}`}
                    className="group relative overflow-hidden rounded-xl border-8 border-white shadow-2xl hover:shadow-3xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 bg-white"
                    onClick={() => setSelectedImage(`/images/moments/${image}`)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                    
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img
                        src={`/images/moments/${image}`}
                        alt={`Live moment ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6 z-20">
                      <p className="text-white font-medium">View Full Size</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Honoured Section */}
          {images.honoured.length > 0 && (
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
                🏆 Honoured
              </h2>
              <div className="space-y-12">
                {images.honoured.map((item, index) => (
                  <div key={`honoured-${index}`}>
                    {/* Caption */}
                    <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
                      {item.caption}
                    </h3>
                    {/* Image */}
                    <div
                      className="group relative overflow-hidden rounded-xl border-8 border-white shadow-2xl hover:shadow-3xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 bg-white"
                      onClick={() => setSelectedImage(`/images/moments/${item.image}`)}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                      
                      <div className="aspect-video overflow-hidden bg-gray-100">
                        <img
                          src={`/images/moments/${item.image}`}
                          alt={item.caption}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6 z-20">
                        <p className="text-white font-medium">View Full Size</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* With Celebrities Section */}
          {images.celebrities.length > 0 && (
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
                ⭐ Celebs
              </h2>
              <div className="space-y-12">
                {images.celebrities.map((item, index) => (
                  <div key={`celebrities-${index}`}>
                    {/* Caption */}
                    <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
                      {item.caption}
                    </h3>
                    {/* Image */}
                    <div
                      className="group relative overflow-hidden rounded-xl border-8 border-white shadow-2xl hover:shadow-3xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 bg-white"
                      onClick={() => setSelectedImage(`/images/moments/${item.image}`)}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                      
                      <div className="aspect-video overflow-hidden bg-gray-100">
                        <img
                          src={`/images/moments/${item.image}`}
                          alt={item.caption}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6 z-20">
                        <p className="text-white font-medium">View Full Size</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default Moments;
